import { calcCompanyActiveYears, getCurrentYear } from "~/helpers/date";
import { findCheapestProduct } from "~/helpers/product";
import type { CountryKey, Faq, Product, ProductName } from "~/types";

interface Options { hasGlobals?: boolean; }

export default function useFaq(country: CountryKey, products: Product[], options: Options = {}) {
  const { tm, rt } = useI18n();
  const domain = useNuxtApp().$domain;
  const { displayPrice } = useFormat();
  const { reviewLinksHtml } = useSocials();
  const { whatsappLink, phone, supportEmail } = useCommunication();

  const faqs = getFaqMessages(`faqs.${country}`);
  if (options.hasGlobals) {
    faqs.push(...getFaqMessages('faqs.global'));
  }

  return {
    faqs
  };

  function getFaqMessages(key: string) {
    const messages = tm(key);
    const answerArgs = buildAnswerArgs(products);
    if (Array.isArray(messages)) {
      return messages.map(faq => buildFaq(faq, answerArgs));
    }
    return [];
  }

  function buildFaq(faq: Faq, answerArgs: Record<string, any>): Faq {
    return {
      q: rt(faq.q, { domain }),
      a: rt(faq.a, answerArgs)
    };
  }

  function buildProductToPriceMap(products: Product[]) {
    return products.reduce((acc, p) => {
      acc[`${p.name}_price`] = displayPrice(p.price);
      return acc;
    }, {} as Record<`${ProductName}_price`, string>)
  }

  function buildAnswerArgs(products: Product[]) {
    const { price: startingPrice, urgencies } = findCheapestProduct(products);

    return {
      ...buildProductToPriceMap(products),
      price: displayPrice(startingPrice),
      urgencyPrice: urgencies?.[0] && displayPrice(urgencies[0].fee),
      currentYear: getCurrentYear(),
      yearsActive: calcCompanyActiveYears(),
      socialLinks: reviewLinksHtml,
      email: supportEmail,
      phone: phone.raw,
      phoneDisplay: phone.display,
      whatsapp: whatsappLink,
      domain
    };
  }
}